import { RestClient } from "./restClient";

export type ProposalKind = "fact" | "skill";
export type ProposalStatus = "pending" | "accepted" | "rejected" | "expired";

/** Shape returned by GET /v1/proposals.
 *
 *  NOTE: the backend serialises the PK as "id" (not "proposal_id").
 *  ProposalsApi.list() normalises it so all consumers can use proposal_id
 *  consistently and the URL helpers (accept/reject) keep working. */
export interface Proposal {
  proposal_id: string;
  kind: ProposalKind;
  trigger_kind: string;
  status: ProposalStatus;
  payload: FactPayload | SkillPayload | Record<string, unknown>;
  created_at: string;
  expires_at: string;
  /** Available when the proposal was created inside a chat turn.
   *  Used to thread the inline card into the matching session
   *  timeline at backfill time. May be null for proposals created
   *  outside any session. */
  session_id?: string | null;
}

export interface FactPayload {
  category: string;
  text: string;
  /** The model's stated reason for proposing the fact. Optional —
   *  not all triggers include evidence. */
  evidence?: string;
}

export interface SkillPayload {
  name: string;
  description: string;
  body: string;
}

export interface ListProposalsOptions {
  kind?: ProposalKind;
  /** Filter to a specific session. The backend supports this; if a
   *  filter is supplied the UI can avoid loading global pending lists. */
  session_id?: string;
}

/** Wraps the /proposals HTTP surface so the chat session hook + cards
 *  don't have to know endpoint shapes. Methods throw RestError on
 *  non-2xx; callers must guard (the chat path always falls back to
 *  silent skip — proposals failing to load must NOT block a turn). */
export class ProposalsApi {
  constructor(private client: RestClient) {}

  list(opts: ListProposalsOptions = {}): Promise<Proposal[]> {
    const qs = new URLSearchParams();
    if (opts.kind) qs.set("kind", opts.kind);
    if (opts.session_id) qs.set("session_id", opts.session_id);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    // Backend returns "id" not "proposal_id" — normalise here so every
    // consumer (tray accept/reject, backfill merge) can use proposal_id.
    return this.client
      .get<{ proposals: Array<Record<string, unknown>> }>(`/core/v1/skills/proposals${suffix}`)
      .then((r) =>
        r.proposals.map((p) => ({
          ...p,
          // Backend serialises the PK as "id"; newer versions also echo it as
          // "proposal_id". Normalise here so every consumer uses proposal_id.
          proposal_id: (p.proposal_id as string) ?? (p.id as string),
        }) as Proposal),
      );
  }

  /** One-click accept (no overrides). The acceptor uses the proposal's
   *  payload verbatim. The response carries the canonical client_payload
   *  for the created entity (FactRow for kind=fact, SkillRow for skill). */
  accept(proposal_id: string): Promise<unknown> {
    return this.client.json<unknown>(
      `/core/v1/skills/proposals/${encodeURIComponent(proposal_id)}/accept`,
      { method: "POST", body: JSON.stringify({}) },
    );
  }

  async reject(proposal_id: string, reason?: string): Promise<void> {
    const body = reason ? JSON.stringify({ reason }) : JSON.stringify({});
    await this.client.json(
      `/core/v1/skills/proposals/${encodeURIComponent(proposal_id)}/reject`,
      { method: "POST", body },
    );
  }
}
